import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import cockpitImg from 'assets/images/cockpit2.png';
import { Button, CloseButton } from 'components';
import { useDisconnect, useHandleConnectClick } from 'hooks';
import { store } from 'store';
import { cutAddress } from 'utils/helpers';

import { Activity, CopiedWallets, OpenPositions, Settings, Space } from './components';
import * as S from './Portfolio.styles';

export const Portfolio: FC = observer(() => {
  const { resetMode } = store.commonStore;
  const { account: web3Account } = store.web3Store;
  const {
    portfolio,
    portfolioResponse,
    wallets,
    positions,
    trades,
    tradesResponse,
    updatePortfolio,
    tab,
    setTab,
  } = store.portfolioStore;
  const { isError } = portfolioResponse;
  const isLoading = portfolioResponse.isLoading && !portfolio;
  const closedPositionsCount = tradesResponse.data?.pagination?.total ?? trades.length;
  const closedPositionsLabel =
    closedPositionsCount > 0 ? `Closed Positions (${closedPositionsCount})` : 'Closed Positions';

  const account = web3Account;
  const isOnline = true;

  const { handleConnectClick } = useHandleConnectClick();
  const { disconnect } = useDisconnect();

  // --- Transition: fade-in → image loads → reveal → data animation → fade-out ---
  type Phase = 'entering' | 'waiting' | 'revealing' | 'ready' | 'exiting';
  const [rootVisible, setRootVisible] = useState(false);
  const [phase, setPhase] = useState<Phase>('entering');
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Trigger Root opacity transition on next frame (so CSS transition fires)
  useEffect(() => {
    const raf = requestAnimationFrame(() => setRootVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // After Root finishes fading in (400ms), move to 'waiting'
  useEffect(() => {
    if (phase !== 'entering') return;
    const t = setTimeout(() => setPhase('waiting'), 450);
    return () => clearTimeout(t);
  }, [phase]);

  // Handle already-cached image (complete before onLoad fires)
  useEffect(() => {
    if (imgRef.current?.complete) setImageLoaded(true);
  }, []);

  // When waiting + image loaded → start revealing
  useEffect(() => {
    if (phase === 'waiting' && imageLoaded) setPhase('revealing');
  }, [phase, imageLoaded]);

  // Exit: overlay fades back in + Root fades out, then unmount
  const handleClose = useCallback(() => {
    setPhase('exiting');
    setRootVisible(false);
  }, []);

  useEffect(() => {
    if (phase !== 'exiting') return;
    const t = setTimeout(() => resetMode(), 700);
    return () => clearTimeout(t);
  }, [phase, resetMode]);

  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  const handleOverlayTransitionEnd = useCallback(() => {
    setPhase((prev) => (prev === 'revealing' ? 'ready' : prev));
  }, []);

  const handleSwitchWallet = useCallback(async () => {
    await disconnect();
    window.setTimeout(() => {
      handleConnectClick();
    }, 0);
  }, [disconnect, handleConnectClick]);

  return (
    <S.Root $visible={rootVisible}>
      <S.Overlay
        $hiding={phase === 'revealing' || phase === 'ready'}
        onTransitionEnd={handleOverlayTransitionEnd}
      />
      <S.SpaceWrapper>
        <Space />
      </S.SpaceWrapper>
      <S.CockpitWrapper>
        <S.CockpitInner>
          <S.CockpitImage ref={imgRef} src={cockpitImg} alt="" onLoad={handleImageLoad} />
          <S.Screen>
            {phase === 'ready' && (
              <S.Portfolio $hasTab={tab !== 'wallet'}>
                {/* ── TOP BAR ── */}
                <S.TopBar>
                  <S.TopBarTitle>Copy Bot</S.TopBarTitle>
                  <S.StatusBadge $isOnline={isOnline}>
                    {isOnline ? 'ONLINE' : 'OFFLINE'}
                  </S.StatusBadge>
                  <S.TopBarSpacer />
                  <S.TopBarNetwork>POLYMARKET</S.TopBarNetwork>
                  {account && (
                    <S.TopBarAccount>
                      <S.AddressLink
                        href={`https://polygonscan.com/address/${account}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cutAddress(account, 4)}
                      </S.AddressLink>
                    </S.TopBarAccount>
                  )}
                  <S.TopBarActions>
                    <S.SyncButton
                      onClick={() => updatePortfolio()}
                      disabled={portfolioResponse.isLoading || !account}
                      aria-label="Refresh"
                      title="Refresh"
                    >
                      <S.SyncButtonLabel>Refresh</S.SyncButtonLabel>
                      <S.SyncButtonIcon>↻</S.SyncButtonIcon>
                    </S.SyncButton>
                    {account && <S.SyncButton onClick={handleSwitchWallet}>Exit</S.SyncButton>}
                  </S.TopBarActions>
                </S.TopBar>

                {/* ── ERROR STATE ── */}
                {isError && (
                  <S.ErrorState>
                    <div>// ERROR</div>
                    <div>Failed to load data</div>
                    <S.HudButton $variant="primary" onClick={() => updatePortfolio()}>
                      Retry
                    </S.HudButton>
                  </S.ErrorState>
                )}

                {/* ── LEFT PANEL — metrics (when no tab open) ── */}
                {tab === 'wallet' && account && !isError && (
                  <S.LeftPanel>
                    <S.MetricBlock>
                      <S.MetricLabel>Balance</S.MetricLabel>
                      <S.MetricValue>
                        {isLoading ? (
                          '...'
                        ) : portfolio ? (
                          <S.DataView
                            value={portfolio.deposit + portfolio.openPositionsValue}
                            type="dollars"
                            toFixed={2}
                          />
                        ) : (
                          '—'
                        )}
                      </S.MetricValue>
                      {portfolio && (
                        <S.MetricSub>
                          {isLoading ? '' : `Proxy: ${cutAddress(portfolio.proxyWallet, 4)}`}
                        </S.MetricSub>
                      )}
                    </S.MetricBlock>

                    <S.MetricBlock>
                      <S.MetricLabel>Deposit</S.MetricLabel>
                      <S.MetricValue>
                        {isLoading ? (
                          '...'
                        ) : portfolio ? (
                          <S.DataView value={portfolio.deposit} type="dollars" toFixed={2} />
                        ) : (
                          '—'
                        )}
                      </S.MetricValue>
                    </S.MetricBlock>

                    <S.MetricBlock>
                      <S.MetricLabel>Open Positions</S.MetricLabel>
                      <S.MetricValue>
                        {isLoading ? (
                          '...'
                        ) : portfolio ? (
                          <S.DataView value={portfolio.openPositionsValue} type="dollars" toFixed={2} />
                        ) : (
                          '—'
                        )}
                      </S.MetricValue>
                    </S.MetricBlock>

                    <S.MetricBlock>
                      <S.MetricLabel>Total PnL</S.MetricLabel>
                      <S.MetricValue>
                        {isLoading ? (
                          '...'
                        ) : portfolio ? (
                          <S.DataView
                            value={portfolio.totalPnl}
                            type="dollars"
                            toFixed={2}
                            hasSign
                            hasColor
                          />
                        ) : (
                          '—'
                        )}
                      </S.MetricValue>
                    </S.MetricBlock>

                    <S.MetricBlock>
                      <S.MetricLabel>ROI</S.MetricLabel>
                      <S.MetricValue>
                        {isLoading ? (
                          '...'
                        ) : portfolio ? (
                          <S.DataView
                            value={portfolio.roi}
                            hasSign
                            hasColor
                            toFixed={1}
                            type="percent"
                          />
                        ) : (
                          '—'
                        )}
                      </S.MetricValue>
                    </S.MetricBlock>
                  </S.LeftPanel>
                )}

                {/* ── RIGHT PANEL — counters + buttons (when no tab open) ── */}
                {tab === 'wallet' && account && !isError && (
                  <S.RightPanel>
                    <S.StatBlock>
                      <S.StatValue>{positions.length}</S.StatValue>
                      <S.StatLabel>Positions</S.StatLabel>
                    </S.StatBlock>

                    <S.StatBlock>
                      <S.StatValue>{wallets.length}</S.StatValue>
                      <S.StatLabel>Leaders</S.StatLabel>
                    </S.StatBlock>

                    <S.RightButtons>
                      <S.HudButton
                        $variant="primary"
                        onClick={() => store.copyFormStore.openCopyModal('deposit')}
                      >
                        Deposit
                      </S.HudButton>
                      <S.HudButton onClick={() => store.copyFormStore.openCopyModal('withdraw')}>
                        Withdraw
                      </S.HudButton>
                    </S.RightButtons>
                  </S.RightPanel>
                )}

                {/* ── CENTER PANEL — tab content ── */}
                {account && tab !== 'wallet' && (
                  <S.CenterPanel>
                    {tab === 'positions' && <OpenPositions />}
                    {tab === 'wallets' && <CopiedWallets />}
                    {tab === 'activity' && <Activity />}
                    {tab === 'settings' && <Settings />}
                  </S.CenterPanel>
                )}

                {/* ── CONNECT WALLET ── */}
                {!account && (
                  <S.ConnectWalletWrapper>
                    <Button onClick={handleConnectClick} isFullWidth>
                      Connect wallet
                    </Button>
                  </S.ConnectWalletWrapper>
                )}

                {/* ── BOTTOM BAR — tabs ── */}
                <S.BottomBar>
                  <S.TabRow>
                    <S.HudTab
                      $isActive={tab === 'wallet'}
                      onClick={() => setTab('wallet')}
                    >
                      Wallet
                    </S.HudTab>
                    <S.HudTab
                      $isActive={tab === 'wallets'}
                      onClick={() => setTab('wallets')}
                    >
                      Leaders ({wallets.length})
                    </S.HudTab>
                    <S.HudTab
                      $isActive={tab === 'positions'}
                      onClick={() => setTab('positions')}
                    >
                      Positions ({positions.length})
                    </S.HudTab>
                    <S.HudTab
                      $isActive={tab === 'activity'}
                      onClick={() => setTab('activity')}
                    >
                      {closedPositionsLabel}
                    </S.HudTab>
                    <S.HudTab
                      $isActive={tab === 'settings'}
                      onClick={() => setTab('settings')}
                    >
                      Settings
                    </S.HudTab>
                  </S.TabRow>
                </S.BottomBar>
              </S.Portfolio>
            )}
          </S.Screen>
        </S.CockpitInner>
      </S.CockpitWrapper>
      <CloseButton onClick={handleClose} />
    </S.Root>
  );
});
